import React, { Suspense, useEffect } from 'react'
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import NotFoundPage from './pages/NotFoundPage'
import { RequireStaff, RequirePermission, RequireAnyPermission } from './components/guards/RequireRole'
import { setNavigateFunction } from './lib/navigation'
import { AdminAuthProvider } from './contexts/AdminAuthContext'
import { ClientPortalAuthProvider } from './contexts/ClientPortalAuthContext'

// ── Stale-chunk guard ─────────────────────────────────────────────────────────
// After a deployment, old cached JS may reference chunk filenames that no longer
// exist, causing 404s and blank screens. This wrapper retries once, then forces
// a full page reload (once) to pick up the new assets.
// Uses a timestamp-based approach so subsequent deployments can still trigger reloads.
function lazyWithRetry(importFn: () => Promise<any>) {
  return React.lazy(() =>
    importFn().catch((err: any) => {
      // Only auto-reload once per 60 seconds to avoid infinite loops
      const key = 'chunk_reload_ts'
      const lastReload = Number(sessionStorage.getItem(key) || '0')
      const now = Date.now()
      if (now - lastReload > 60000) {
        sessionStorage.setItem(key, String(now))
        window.location.reload()
        // Return a never-resolving promise so React doesn't render the error
        return new Promise(() => {})
      }
      // Already reloaded recently — throw so the error boundary can handle it
      throw err
    })
  )
}

// Admin Portal pages
const AdminLogin = lazyWithRetry(() => import('./pages/admin/AdminLogin'))
const AdminLayout = lazyWithRetry(() => import('./pages/admin/AdminLayout'))
const AdminOrganizations = lazyWithRetry(() => import('./pages/admin/Organizations'))
const AdminAuditLogs = lazyWithRetry(() => import('./pages/admin/AuditLogs'))
const AdminUsers = lazyWithRetry(() => import('./pages/admin/AdminUsers'))
const AdminOrgDetail = lazyWithRetry(() => import('./pages/admin/OrganizationDetail'))
const AdminUserPermissions = lazyWithRetry(() => import('./pages/admin/UserPermissions'))
const AcceptInvite = lazyWithRetry(() => import('./pages/admin/AcceptInvite'))
const ResetPassword = lazyWithRetry(() => import('./pages/admin/ResetAdminPassword'))

// Lazy-load all base44 pages for code splitting
const Home = lazyWithRetry(() => import('./pages/Home'))
const AdvisorDashboard = lazyWithRetry(() => import('./pages/AdvisorDashboard'))
const Clients = lazyWithRetry(() => import('./pages/Clients'))
const ClientDetail = lazyWithRetry(() => import('./pages/ClientDetail'))
const ClientFinancialData = lazyWithRetry(() => import('./pages/ClientFinancialData'))
const PlanningCenter = lazyWithRetry(() => import('./pages/PlanningCenter'))
const PlanningCenterLanding = lazyWithRetry(() => import('./pages/PlanningCenterLanding'))
const Tasks = lazyWithRetry(() => import('./pages/Tasks'))
const Calendar = lazyWithRetry(() => import('./pages/Calendar'))
const CalendarSettings = lazyWithRetry(() => import('./pages/CalendarSettings'))
const Workload = lazyWithRetry(() => import('./pages/Workload'))
const Notifications = lazyWithRetry(() => import('./pages/Notifications'))
const NotificationSettings = lazyWithRetry(() => import('./pages/NotificationSettings'))
const OperationsHub = lazyWithRetry(() => import('./pages/OperationsHub'))
const Cases = lazyWithRetry(() => import('./pages/Cases'))
const CaseTemplates = lazyWithRetry(() => import('./pages/CaseTemplates'))
const Goals = lazyWithRetry(() => import('./pages/Goals'))
const GoalTemplates = lazyWithRetry(() => import('./pages/GoalTemplates'))
const Reviews = lazyWithRetry(() => import('./pages/Reviews'))
const ReviewTemplates = lazyWithRetry(() => import('./pages/ReviewTemplates'))
const ReviewComparison = lazyWithRetry(() => import('./pages/ReviewComparison'))
const AdvisorReviewPresentation = lazyWithRetry(() => import('./pages/AdvisorReviewPresentation'))
const ClientReviewPresentation = lazyWithRetry(() => import('./pages/ClientReviewPresentation'))
const Households = lazyWithRetry(() => import('./pages/Households'))
const HouseholdDetail = lazyWithRetry(() => import('./pages/HouseholdDetail'))
const HouseholdFinancialData = lazyWithRetry(() => import('./pages/HouseholdFinancialData'))
const Leads = lazyWithRetry(() => import('./pages/Leads'))
const Lead = lazyWithRetry(() => import('./pages/Lead'))
const LeadPipelineSettings = lazyWithRetry(() => import('./pages/LeadPipelineSettings'))
const Actions = lazyWithRetry(() => import('./pages/Actions'))
const ActionTemplates = lazyWithRetry(() => import('./pages/ActionTemplates'))
const Messages = lazyWithRetry(() => import('./pages/Messages'))
const Communications = lazyWithRetry(() => import('./pages/Communications'))
const Inbox = lazyWithRetry(() => import('./pages/Inbox'))
const MarketingCampaigns = lazyWithRetry(() => import('./pages/MarketingCampaigns'))
const Forms = lazyWithRetry(() => import('./pages/Forms'))
const Questionnaires = lazyWithRetry(() => import('./pages/Questionnaires'))
const Reports = lazyWithRetry(() => import('./pages/Reports'))
const Tools = lazyWithRetry(() => import('./pages/Tools'))
const DocumentSigning = lazyWithRetry(() => import('./pages/DocumentSigning'))
const DocumentTemplateList = lazyWithRetry(() => import('./pages/DocumentTemplateList'))
const GlobalDocumentFolders = lazyWithRetry(() => import('./pages/GlobalDocumentFolders'))
const ComplianceCenter = lazyWithRetry(() => import('./pages/ComplianceCenter'))
const Marketplace = lazyWithRetry(() => import('./pages/Marketplace'))
const MarketplaceAnalytics = lazyWithRetry(() => import('./pages/MarketplaceAnalytics'))
const CreatePackage = lazyWithRetry(() => import('./pages/CreatePackage'))
const EditPackage = lazyWithRetry(() => import('./pages/EditPackage'))
const PackageDetail = lazyWithRetry(() => import('./pages/PackageDetail'))
const MyPackages = lazyWithRetry(() => import('./pages/MyPackages'))
const MyPurchases = lazyWithRetry(() => import('./pages/MyPurchases'))
const Automations = lazyWithRetry(() => import('./pages/Automations'))
const AutomationMonitor = lazyWithRetry(() => import('./pages/AutomationMonitor'))
const IntelligenceCenter = lazyWithRetry(() => import('./pages/IntelligenceCenter'))
const TaxPlanningHub = lazyWithRetry(() => import('./pages/TaxPlanningHub'))
const TaskConfiguration = lazyWithRetry(() => import('./pages/TaskConfiguration'))
const TaskTemplates = lazyWithRetry(() => import('./pages/TaskTemplates'))
const TaskflowEditor = lazyWithRetry(() => import('./pages/TaskflowEditor'))
const InternalWorkflows = lazyWithRetry(() => import('./pages/InternalWorkflows'))
const WorkflowBuilder = lazyWithRetry(() => import('./pages/WorkflowBuilder'))
const WorkflowExecutor = lazyWithRetry(() => import('./pages/WorkflowExecutor'))
const ClientPortal = lazyWithRetry(() => import('./pages/ClientPortal'))
const ClientSettings = lazyWithRetry(() => import('./pages/ClientSettings'))
const ObjectSegments = lazyWithRetry(() => import('./pages/ObjectSegments'))
const OnboardingTemplates = lazyWithRetry(() => import('./pages/OnboardingTemplates'))
const FinancialScoringSettings = lazyWithRetry(() => import('./pages/FinancialScoringSettings'))
const ScoringMetricsRegistry = lazyWithRetry(() => import('./pages/ScoringMetricsRegistry'))
const GlobalSettings = lazyWithRetry(() => import('./pages/GlobalSettings'))
const IntegrationSettings = lazyWithRetry(() => import('./pages/IntegrationSettings'))
const UserSettings = lazyWithRetry(() => import('./pages/UserSettings'))
const UserManagement = lazyWithRetry(() => import('./pages/UserManagement'))
const RoleManagement = lazyWithRetry(() => import('./pages/RoleManagement'))
const TeamManagement = lazyWithRetry(() => import('./pages/TeamManagement'))
const AdvisorStructure = lazyWithRetry(() => import('./pages/AdvisorStructure'))
const LocationManagement = lazyWithRetry(() => import('./pages/LocationManagement'))
const AdvisorOrgHierarchy = lazyWithRetry(() => import('./pages/OrgHierarchy'))
const OrgHierarchy = lazyWithRetry(() => import('./pages/admin/OrgHierarchy'))
const AdminUserLocationAccess = lazyWithRetry(() => import('./pages/admin/UserLocationAccess'))
const QuickActionsSettings = lazyWithRetry(() => import('./pages/QuickActionsSettings'))
const TenantManagement = lazyWithRetry(() => import('./pages/TenantManagement'))
const Changelog = lazyWithRetry(() => import('./pages/Changelog'))
const PublicAdvisorBooking = lazyWithRetry(() => import('./pages/PublicAdvisorBooking'))
const PublicLeadForm = lazyWithRetry(() => import('./pages/PublicLeadForm'))
const FeatureTesting = lazyWithRetry(() => import('./pages/FeatureTesting'))

// Client Portal pages (separate auth — mirrors admin portal pattern)
const PortalLogin = lazyWithRetry(() => import('./pages/clientportal/PortalLogin'))
const PortalLayout = lazyWithRetry(() => import('./pages/clientportal/PortalLayout'))
const PortalDashboard = lazyWithRetry(() => import('./pages/clientportal/PortalDashboard'))
const PortalForgotPassword = lazyWithRetry(() => import('./pages/clientportal/PortalForgotPassword'))
const PortalGoals = lazyWithRetry(() => import('./pages/clientportal/PortalGoals'))
const PortalActions = lazyWithRetry(() => import('./pages/clientportal/PortalActions'))
const PortalMessages = lazyWithRetry(() => import('./pages/clientportal/PortalMessages'))
const PortalDocuments = lazyWithRetry(() => import('./pages/clientportal/PortalDocuments'))
const PortalAppointmentsPage = lazyWithRetry(() => import('./pages/clientportal/PortalAppointmentsPage'))
const PortalReviews = lazyWithRetry(() => import('./pages/clientportal/PortalReviews'))
const PortalFinancialHealth = lazyWithRetry(() => import('./pages/clientportal/PortalFinancialHealth'))
const PortalAssessments = lazyWithRetry(() => import('./pages/clientportal/PortalAssessments'))
const PortalAccounts = lazyWithRetry(() => import('./pages/clientportal/PortalAccounts'))
const PortalReportsPage = lazyWithRetry(() => import('./pages/clientportal/PortalReportsPage'))
const PortalCases = lazyWithRetry(() => import('./pages/clientportal/PortalCases'))

// Domain helpers
const isLocalDev = () => {
  const host = window.location.hostname
  return host === 'localhost' || host.startsWith('127.')
}

const isClientPortalDomain = () => {
  const host = window.location.hostname
  return host.startsWith('clientportal.') || host.startsWith('dev-clientportal.')
}

const isAdminDomain = () => {
  const host = window.location.hostname
  return host.startsWith('admin.')
}

/** Block access to portal routes on non-portal domains (localhost allowed for dev) */
function ClientPortalDomainGuard({ children }: { children: React.ReactNode }) {
  if (!isClientPortalDomain() && !isLocalDev()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-sm px-6">
          <h1 className="text-base font-semibold text-slate-700">Access Denied</h1>
          <p className="text-sm text-slate-500 mt-2">
            The client portal is only accessible at{' '}
            <span className="font-medium">clientportal.vestolio.com</span>.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

/** Redirect to correct login if someone hits advisor app on a portal or admin domain (not localhost) */
function BlockOnPortalDomain({ children }: { children: React.ReactNode }) {
  if (isClientPortalDomain()) {
    return <Navigate to="/portal/login" replace />
  }
  if (isAdminDomain()) {
    return <Navigate to="/admin/login" replace />
  }
  return <>{children}</>
}

/** Redirect admin/portal domain users away from advisor routes to their correct login */
function BlockOnSpecialDomain({ children }: { children: React.ReactNode }) {
  if (!isLocalDev()) {
    if (isAdminDomain()) {
      return <Navigate to="/admin/login" replace />
    }
    if (isClientPortalDomain()) {
      return <Navigate to="/portal/login" replace />
    }
  }
  return <>{children}</>
}

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  )
}

function App() {
  const navigate = useNavigate()

  useEffect(() => {
    setNavigateFunction(navigate)
  }, [navigate])

  return (
    <AuthProvider>
      <SocketProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Admin Portal routes */}
          <Route path="/admin/login" element={<AdminAuthProvider><AdminLogin /></AdminAuthProvider>} />
          <Route path="/admin/accept-invite" element={<AcceptInvite />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<AdminAuthProvider><AdminLayout /></AdminAuthProvider>}>
            <Route index element={<AdminOrganizations />} />
            <Route path="organizations/:orgId" element={<AdminOrgDetail />} />
            <Route path="organizations/:orgId/users/:userId/permissions" element={<AdminUserPermissions />} />
            <Route path="organizations/:orgId/users/:userId/location-access" element={<AdminUserLocationAccess />} />
            <Route path="organizations/:orgId/hierarchy" element={<OrgHierarchy />} />
            <Route path="admin-users" element={<AdminUsers />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
          </Route>

          {/* Client Portal routes — only accessible on clientportal.vestolio.com */}
          <Route path="/portal/login" element={<ClientPortalDomainGuard><ClientPortalAuthProvider><PortalLogin /></ClientPortalAuthProvider></ClientPortalDomainGuard>} />
          <Route path="/portal/forgot-password" element={<ClientPortalDomainGuard><ClientPortalAuthProvider><PortalForgotPassword /></ClientPortalAuthProvider></ClientPortalDomainGuard>} />
          <Route path="/portal" element={<ClientPortalDomainGuard><ClientPortalAuthProvider><PortalLayout /></ClientPortalAuthProvider></ClientPortalDomainGuard>}>
            <Route index element={<PortalDashboard />} />
            <Route path="goals" element={<PortalGoals />} />
            <Route path="actions" element={<PortalActions />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="appointments" element={<PortalAppointmentsPage />} />
            <Route path="reviews" element={<PortalReviews />} />
            <Route path="financial-health" element={<PortalFinancialHealth />} />
            <Route path="assessments" element={<PortalAssessments />} />
            <Route path="accounts" element={<PortalAccounts />} />
            <Route path="cases" element={<PortalCases />} />
            <Route path="reports" element={<PortalReportsPage />} />
          </Route>

          {/* Public routes — blocked on client portal domain */}
          <Route path="/login" element={<BlockOnPortalDomain><LoginPage /></BlockOnPortalDomain>} />
          {/* Registration disabled — accounts by invite only */}
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/verify-email" element={<Navigate to="/login" replace />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/publicadvisorbooking" element={<PublicAdvisorBooking />} />
          <Route path="/publicleadform" element={<PublicLeadForm />} />
          {/* Legacy client registration — redirect to new accept-invite flow */}
          <Route path="/clientregistration" element={<Navigate to="/accept-invite" replace />} />
          <Route path="/advisor-review-presentation" element={<AdvisorReviewPresentation />} />
          <Route path="/client-review-presentation" element={<ClientReviewPresentation />} />
          
          {/* Protected routes — block on admin/portal subdomains */}
          <Route path="/" element={<BlockOnSpecialDomain><Layout /></BlockOnSpecialDomain>}>
            {/* Dashboard - accessible to all authenticated users */}
            <Route index element={<Home />} />
            <Route path="home" element={<Home />} />
            <Route path="advisordashboard" element={<RequireStaff><AdvisorDashboard /></RequireStaff>} />
            
            {/* Client Portal - accessible to all (ClientPortal handles role logic internally) */}
            <Route path="clientportal" element={<ClientPortal />} />
            
            {/* Clients - permission gated */}
            <Route path="clients" element={<RequirePermission module="clients" action="view"><Clients /></RequirePermission>} />
            <Route path="clientdetail" element={<RequirePermission module="clients" action="view"><ClientDetail /></RequirePermission>} />
            <Route path="clients/:id" element={<RequirePermission module="clients" action="view"><ClientDetail /></RequirePermission>} />
            <Route path="clientfinancialdata" element={<RequirePermission module="financial_data" action="view"><ClientFinancialData /></RequirePermission>} />
            <Route path="data-center" element={<RequirePermission module="financial_data" action="view"><ClientFinancialData /></RequirePermission>} />
            <Route path="planningcenter" element={<RequirePermission module="financial_data" action="view"><PlanningCenter /></RequirePermission>} />
            <Route path="planning-center" element={<RequirePermission module="financial_data" action="view"><PlanningCenter /></RequirePermission>} />
            <Route path="planningcenterlanding" element={<RequirePermission module="financial_data" action="view"><PlanningCenterLanding /></RequirePermission>} />
            <Route path="planning-center-landing" element={<RequirePermission module="financial_data" action="view"><PlanningCenterLanding /></RequirePermission>} />
            {/* Client/Segment admin settings */}
            <Route path="clientsettings" element={<RequirePermission module="settings" action="data_management"><ClientSettings /></RequirePermission>} />
            <Route path="segments" element={<RequirePermission module="segments" action="view"><ObjectSegments /></RequirePermission>} />
            <Route path="clientsegmentmanagement" element={<RequirePermission module="segments" action="view"><ObjectSegments /></RequirePermission>} />
            <Route path="onboardingtemplates" element={<RequirePermission module="templates" action="view"><OnboardingTemplates /></RequirePermission>} />
            <Route path="financialscoringsettings" element={<RequirePermission module="settings" action="scoring"><FinancialScoringSettings /></RequirePermission>} />
            <Route path="scoringmetricsregistry" element={<RequirePermission module="settings" action="scoring"><ScoringMetricsRegistry /></RequirePermission>} />
            
            {/* Households - permission gated */}
            <Route path="households" element={<RequirePermission module="clients" action="view"><Households /></RequirePermission>} />
            <Route path="householddetail" element={<RequirePermission module="clients" action="view"><HouseholdDetail /></RequirePermission>} />
            <Route path="householdfinancialdata" element={<RequirePermission module="clients" action="view"><HouseholdFinancialData /></RequirePermission>} />
            
            {/* Leads - permission gated */}
            <Route path="leads" element={<RequirePermission module="clients" action="view"><Leads /></RequirePermission>} />
            <Route path="lead" element={<RequirePermission module="clients" action="view"><Lead /></RequirePermission>} />
            <Route path="leadpipelinesettings" element={<RequirePermission module="settings" action="data_management"><LeadPipelineSettings /></RequirePermission>} />
            
            {/* Tasks & Actions - permission gated */}
            <Route path="tasks" element={<RequirePermission module="tasks" action="view"><Tasks /></RequirePermission>} />
            <Route path="actions" element={<RequirePermission module="tasks" action="view"><Actions /></RequirePermission>} />
            <Route path="actiontemplates" element={<RequirePermission module="templates" action="view"><ActionTemplates /></RequirePermission>} />
            <Route path="taskconfiguration" element={<RequirePermission module="settings" action="system"><TaskConfiguration /></RequirePermission>} />
            <Route path="tasktemplates" element={<RequirePermission module="templates" action="view"><TaskTemplates /></RequirePermission>} />
            <Route path="taskfloweditor" element={<RequirePermission module="templates" action="view"><TaskflowEditor /></RequirePermission>} />
            
            {/* Goals - permission gated */}
            <Route path="goals" element={<RequirePermission module="goals" action="view"><Goals /></RequirePermission>} />
            <Route path="goaltemplates" element={<RequirePermission module="templates" action="view"><GoalTemplates /></RequirePermission>} />
            
            {/* Calendar - permission gated */}
            <Route path="calendar" element={<RequirePermission module="calendar" action="view"><Calendar /></RequirePermission>} />
            <Route path="calendarsettings" element={<RequirePermission module="calendar" action="view"><CalendarSettings /></RequirePermission>} />
            
            {/* Workload - permission gated */}
            <Route path="workload" element={<RequirePermission module="tasks" action="view"><Workload /></RequirePermission>} />
            
            {/* Operations - permission gated */}
            <Route path="operationshub" element={<RequirePermission module="workflows" action="view"><OperationsHub /></RequirePermission>} />
            <Route path="operations" element={<RequirePermission module="workflows" action="view"><OperationsHub /></RequirePermission>} />
            
            {/* Cases - permission gated */}
            <Route path="cases" element={<RequirePermission module="cases" action="view"><Cases /></RequirePermission>} />
            <Route path="casetemplates" element={<RequirePermission module="templates" action="view"><CaseTemplates /></RequirePermission>} />
            
            {/* Reviews - permission gated */}
            <Route path="reviews" element={<RequirePermission module="reviews" action="view"><Reviews /></RequirePermission>} />
            <Route path="reviewtemplates" element={<RequirePermission module="templates" action="view"><ReviewTemplates /></RequirePermission>} />
            <Route path="reviewcomparison" element={<RequirePermission module="reviews" action="view"><ReviewComparison /></RequirePermission>} />
            <Route path="advisorreviewpresentation" element={<RequirePermission module="reviews" action="view"><AdvisorReviewPresentation /></RequirePermission>} />
            <Route path="clientreviewpresentation" element={<RequirePermission module="reviews" action="view"><ClientReviewPresentation /></RequirePermission>} />
            
            {/* Communications - permission gated */}
            <Route path="inbox" element={<RequirePermission module="communications" action="view"><Inbox /></RequirePermission>} />
            <Route path="messages" element={<RequirePermission module="communications" action="view"><Messages /></RequirePermission>} />
            <Route path="communications" element={<RequirePermission module="communications" action="view"><Communications /></RequirePermission>} />
            <Route path="marketingcampaigns" element={<RequirePermission module="campaigns" action="view"><MarketingCampaigns /></RequirePermission>} />
            
            {/* Documents & Forms - permission gated */}
            <Route path="forms" element={<RequirePermission module="forms" action="view"><Forms /></RequirePermission>} />
            <Route path="questionnaires" element={<RequirePermission module="forms" action="view"><Questionnaires /></RequirePermission>} />
            <Route path="documentsigning" element={<RequirePermission module="documents" action="view"><DocumentSigning /></RequirePermission>} />
            <Route path="documenttemplatelist" element={<RequirePermission module="documents" action="view"><DocumentTemplateList /></RequirePermission>} />
            <Route path="globaldocumentfolders" element={<RequirePermission module="documents" action="view"><GlobalDocumentFolders /></RequirePermission>} />
            
            {/* Reports & Tools - permission gated */}
            <Route path="reports" element={<RequirePermission module="reports" action="view"><Reports /></RequirePermission>} />
            <Route path="tools" element={<RequirePermission module="tools" action="view"><Tools /></RequirePermission>} />
            <Route path="intelligencecenter" element={<RequirePermission module="settings" action="intelligence_center"><IntelligenceCenter /></RequirePermission>} />
            <Route path="taxplanninghub" element={<RequirePermission module="tools" action="view"><TaxPlanningHub /></RequirePermission>} />
            
            {/* Compliance - permission gated */}
            <Route path="compliancecenter" element={<RequirePermission module="compliance" action="view"><ComplianceCenter /></RequirePermission>} />
            
            {/* Marketplace - permission gated */}
            <Route path="marketplace" element={<RequirePermission module="marketplace" action="view"><Marketplace /></RequirePermission>} />
            <Route path="marketplaceanalytics" element={<RequirePermission module="marketplace" action="view"><MarketplaceAnalytics /></RequirePermission>} />
            <Route path="createpackage" element={<RequirePermission module="marketplace" action="create"><CreatePackage /></RequirePermission>} />
            <Route path="editpackage" element={<RequirePermission module="marketplace" action="edit"><EditPackage /></RequirePermission>} />
            <Route path="packagedetail" element={<RequirePermission module="marketplace" action="view"><PackageDetail /></RequirePermission>} />
            <Route path="mypackages" element={<RequirePermission module="marketplace" action="view"><MyPackages /></RequirePermission>} />
            <Route path="mypurchases" element={<RequirePermission module="marketplace" action="view"><MyPurchases /></RequirePermission>} />
            
            {/* Automations & Workflows - permission gated */}
            <Route path="automations" element={<RequirePermission module="workflows" action="view"><Automations /></RequirePermission>} />
            <Route path="automationmonitor" element={<RequirePermission module="workflows" action="view"><AutomationMonitor /></RequirePermission>} />
            <Route path="internalworkflows" element={<RequirePermission module="workflows" action="view"><InternalWorkflows /></RequirePermission>} />
            <Route path="workflowbuilder" element={<RequirePermission module="workflows" action="view"><WorkflowBuilder /></RequirePermission>} />
            <Route path="workflowexecutor" element={<RequirePermission module="workflows" action="view"><WorkflowExecutor /></RequirePermission>} />
            
            {/* Notifications - all authenticated users */}
            <Route path="notifications" element={<Notifications />} />
            <Route path="notificationsettings" element={<RequirePermission module="settings" action="system"><NotificationSettings /></RequirePermission>} />
            
            {/* Settings & Admin - permission gated */}
            <Route path="globalsettings" element={<RequireAnyPermission checks={[['settings','system'],['settings','integrations'],['settings','scoring'],['settings','investments'],['settings','data_management'],['settings','templates'],['settings','team_roles'],['settings','maintenance'],['settings','intelligence_center']]}><GlobalSettings /></RequireAnyPermission>} />
            <Route path="integrationsettings" element={<RequirePermission module="integrations" action="view"><IntegrationSettings /></RequirePermission>} />
            <Route path="usersettings" element={<UserSettings />} />
            <Route path="usermanagement" element={<RequirePermission module="users" action="view"><UserManagement /></RequirePermission>} />
            <Route path="rolemanagement" element={<RequirePermission module="users" action="view"><RoleManagement /></RequirePermission>} />
            <Route path="teammanagement" element={<RequirePermission module="teams" action="view"><TeamManagement /></RequirePermission>} />
            <Route path="advisorstructure" element={<RequirePermission module="users" action="view"><AdvisorStructure /></RequirePermission>} />
            <Route path="locationmanagement" element={<RequirePermission module="settings" action="team_roles"><LocationManagement /></RequirePermission>} />
            <Route path="org-hierarchy" element={<RequirePermission module="settings" action="team_roles"><AdvisorOrgHierarchy /></RequirePermission>} />
            <Route path="quickactionssettings" element={<RequirePermission module="settings" action="system"><QuickActionsSettings /></RequirePermission>} />
            <Route path="tenantmanagement" element={<RequirePermission module="settings" action="maintenance"><TenantManagement /></RequirePermission>} />
            <Route path="changelog" element={<RequirePermission module="settings" action="maintenance"><Changelog /></RequirePermission>} />
            <Route path="featuretesting" element={<RequirePermission module="settings" action="maintenance"><FeatureTesting /></RequirePermission>} />
          </Route>
          
          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </SocketProvider>
    </AuthProvider>
  )
}

export default App